import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowRight,
    Building2,
    CalendarClock,
    Camera,
    CheckCircle2,
    Crosshair,
    FileText,
    Loader2,
    MapPin,
    MessageCircle,
    Phone,
    UserRound,
} from 'lucide-react';

import { getLeadById, updateLeadLocation } from '@/app/services/leadService';
import {
    buildMapsUrl,
    formatCoordinates,
    geoLocationToMapsUrl,
    getCurrentCoordinates,
    type Coordinates,
} from '@/app/lib/geolocation';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';
import {
    LEAD_SOURCE_LABELS,
    LEAD_STATUS_LABELS,
    LEAD_TYPE_LABELS,
    type LeadActivity,
    type LeadStatus,
} from '@/app/types/lead';
import {
    SELECTABLE_STATUSES,
    formatFollowUp,
    formatRelativeTime,
    statusVariant,
} from './leadUi';
import { whatsappUrl } from '@/app/lib/phone';
import { AddActivitySheet } from './components/AddActivitySheet';
import { ConvertLeadSheet } from './components/ConvertLeadSheet';
import { LeadPhotoStrip } from './components/LeadPhotoStrip';

export default function LeadDetailPage() {
    const { id } = useParams<{ id: string }>();
    const queryClient = useQueryClient();

    const [activityOpen, setActivityOpen] = useState(false);
    const [presetStatus, setPresetStatus] = useState<LeadStatus | null>(null);
    const [convertOpen, setConvertOpen] = useState(false);
    const [locating, setLocating] = useState(false);
    const [captured, setCaptured] = useState<Coordinates | null>(null);
    const [confirmReplace, setConfirmReplace] = useState(false);

    const { data: lead, isLoading } = useQuery({
        queryKey: ['lead', Number(id)],
        queryFn: () => getLeadById(Number(id)),
        enabled: !!id,
    });

    const locationMutation = useMutation({
        mutationFn: (geo: string) => updateLeadLocation(Number(id), geo),
        onSuccess: () => {
            toast.success('Location saved');
            queryClient.invalidateQueries({ queryKey: ['lead', Number(id)] });
            setCaptured(null);
            setConfirmReplace(false);
        },
        onError: () => toast.error('Could not save the location'),
    });

    const handleCaptureLocation = async () => {
        setLocating(true);
        try {
            setCaptured(await getCurrentCoordinates());
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not get location');
        } finally {
            setLocating(false);
        }
    };

    const handleSaveCaptured = () => {
        if (!captured || !lead) return;
        // Same guard the party screen uses: don't silently overwrite a good fix.
        if (lead.geo_location && !confirmReplace) {
            setConfirmReplace(true);
            return;
        }
        locationMutation.mutate(formatCoordinates(captured.lat, captured.lng));
    };

    if (isLoading) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Lead" showBack backPath="/leads" />
                <div className="p-4 space-y-3">
                    <MobileSkeleton className="h-28 w-full rounded-xl" />
                    <MobileSkeleton className="h-14 w-full rounded-xl" />
                    <MobileSkeleton className="h-40 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    if (!lead) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Lead" showBack backPath="/leads" />
                <div className="p-8 text-center text-muted-foreground">Lead not found</div>
            </div>
        );
    }

    const followUpInfo = formatFollowUp(lead.next_follow_up_date);
    const isConverted = lead.status === 'converted';

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title={lead.business_name} showBack backPath="/leads" />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-4">
                    {/* Hero */}
                    <div className="mobile-card">
                        <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg font-semibold text-primary">
                                    {lead.business_name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-foreground">{lead.business_name}</p>
                                <p className="text-sm text-muted-foreground">
                                    {[
                                        lead.area,
                                        lead.lead_type ? LEAD_TYPE_LABELS[lead.lead_type] : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <MobileBadge variant={statusVariant[lead.status]}>
                                        {LEAD_STATUS_LABELS[lead.status]}
                                    </MobileBadge>
                                    {!isConverted && (
                                        <span
                                            className={cn(
                                                'text-xs font-medium',
                                                followUpInfo.overdue
                                                    ? 'text-destructive'
                                                    : 'text-muted-foreground'
                                            )}
                                        >
                                            {followUpInfo.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {lead.estimated_monthly_value && (
                            <p className="text-xs text-muted-foreground mt-3">
                                Est. ₹
                                {Number(lead.estimated_monthly_value).toLocaleString('en-IN')}/month
                            </p>
                        )}
                    </div>

                    {/* Quick actions */}
                    <div className="grid grid-cols-4 gap-2">
                        <ActionButton
                            icon={<Phone className="h-5 w-5" />}
                            label="Call"
                            disabled={!lead.phone}
                            onClick={() => {
                                if (lead.phone) window.location.href = `tel:${lead.phone}`;
                            }}
                        />
                        <ActionButton
                            icon={<MessageCircle className="h-5 w-5" />}
                            label="WhatsApp"
                            disabled={!lead.phone}
                            onClick={() => {
                                if (lead.phone) {
                                    window.open(
                                        whatsappUrl(
                                            lead.phone,
                                            `Hello ${lead.contact_person || ''}`.trim()
                                        ),
                                        '_blank'
                                    );
                                }
                            }}
                        />
                        <ActionButton
                            icon={<MapPin className="h-5 w-5" />}
                            label="Map"
                            disabled={!lead.geo_location}
                            onClick={() => {
                                if (lead.geo_location) {
                                    window.open(geoLocationToMapsUrl(lead.geo_location), '_blank');
                                }
                            }}
                        />
                        <ActionButton
                            icon={<CalendarClock className="h-5 w-5" />}
                            label="Follow up"
                            disabled={isConverted}
                            onClick={() => {
                                setPresetStatus(null);
                                setActivityOpen(true);
                            }}
                        />
                    </div>

                    {/* Stage chips — tapping one opens the follow-up sheet, so the
                        status can never move without a note explaining why. */}
                    {!isConverted && (
                        <div>
                            <p className="mobile-section-header mb-2">Stage</p>
                            <div className="flex gap-2 flex-wrap">
                                {SELECTABLE_STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => {
                                            if (s === lead.status) return;
                                            setPresetStatus(s);
                                            setActivityOpen(true);
                                        }}
                                        className={cn(
                                            'px-3 py-2 rounded-full text-sm font-medium',
                                            lead.status === s
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-secondary text-muted-foreground active:bg-accent'
                                        )}
                                    >
                                        {LEAD_STATUS_LABELS[s]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* In the content flow, NOT a sticky footer: this screen renders
                        inside MobileLayout, whose fixed tab bar owns the bottom of the
                        viewport and would sit on top of a sticky bar, swallowing the tap. */}
                    {!isConverted && (
                        <MobileButton className="w-full" onClick={() => setConvertOpen(true)}>
                            Convert to customer
                        </MobileButton>
                    )}

                    {isConverted && lead.converted_party_id && (
                        <Link to={`/parties/${lead.converted_party_id}`} className="block">
                            <div className="mobile-card border-green-500/40 bg-green-500/5 active:bg-accent">
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">Converted to customer</p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {lead.converted_party?.name ?? 'View customer'}
                                        </p>
                                    </div>
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                </div>
                            </div>
                        </Link>
                    )}

                    {/* Details */}
                    <div>
                        <p className="mobile-section-header mb-2">Details</p>
                        <MobileCardGroup>
                            {lead.contact_person && (
                                <MobileCardRow>
                                    <Detail label="Contact" value={lead.contact_person} />
                                </MobileCardRow>
                            )}
                            {lead.phone && (
                                <MobileCardRow>
                                    <Detail label="Phone" value={lead.phone} />
                                </MobileCardRow>
                            )}
                            {lead.alt_phone && (
                                <MobileCardRow>
                                    <Detail label="Alternate phone" value={lead.alt_phone} />
                                </MobileCardRow>
                            )}
                            {lead.address && (
                                <MobileCardRow>
                                    <Detail label="Address" value={lead.address} />
                                </MobileCardRow>
                            )}
                            <MobileCardRow>
                                <Detail label="Source" value={LEAD_SOURCE_LABELS[lead.source]} />
                            </MobileCardRow>
                            {lead.assigned_to && (
                                <MobileCardRow>
                                    <Detail label="Owner" value={lead.assigned_to.name} />
                                </MobileCardRow>
                            )}
                            <MobileCardRow onClick={locating ? undefined : handleCaptureLocation}>
                                <div className="flex items-center gap-3">
                                    {locating ? (
                                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                                    ) : (
                                        <Crosshair className="h-5 w-5 text-primary" />
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-xs text-muted-foreground">Location</p>
                                        <p className="text-sm truncate">
                                            {locating
                                                ? 'Getting location…'
                                                : (lead.geo_location ?? 'Tap to capture')}
                                        </p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>

                    {lead.notes && (
                        <div>
                            <p className="mobile-section-header mb-2">Notes</p>
                            <div className="mobile-card">
                                <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
                            </div>
                        </div>
                    )}

                    <LeadPhotoStrip leadId={lead.id} siteId={lead.site_id} />

                    {/* One timeline: comments and stage changes in the same list, which
                        is why transitions live on the activity row. */}
                    <div>
                        <p className="mobile-section-header mb-2">
                            Timeline ({lead.activities.length})
                        </p>
                        <div className="space-y-3">
                            {lead.activities.map((activity) => (
                                <TimelineItem key={activity.id} activity={activity} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <AddActivitySheet
                leadId={lead.id}
                open={activityOpen}
                onClose={() => setActivityOpen(false)}
                currentStatus={lead.status}
                presetStatus={presetStatus}
            />

            <ConvertLeadSheet lead={lead} open={convertOpen} onClose={() => setConvertOpen(false)} />

            <MobileBottomSheet
                open={!!captured}
                onClose={() => {
                    setCaptured(null);
                    setConfirmReplace(false);
                }}
                title={confirmReplace ? 'Replace saved location?' : 'Confirm location'}
                heightMode="auto"
            >
                {captured && (
                    <div className="p-4 space-y-4">
                        <div>
                            <p className="text-lg font-semibold">
                                {captured.lat.toFixed(6)}, {captured.lng.toFixed(6)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Accuracy ±{Math.round(captured.accuracy)} m
                            </p>
                        </div>
                        {confirmReplace && (
                            <p className="text-sm text-yellow-400">
                                This lead already has a saved location. Saving replaces it.
                            </p>
                        )}
                        <a
                            href={buildMapsUrl(captured.lat, captured.lng)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary font-medium"
                        >
                            <MapPin className="h-4 w-4" /> Verify in Maps
                        </a>
                        <div className="flex gap-2">
                            <MobileButton
                                className="w-full"
                                isLoading={locationMutation.isPending}
                                onClick={handleSaveCaptured}
                            >
                                {confirmReplace ? 'Replace' : 'Save'}
                            </MobileButton>
                            <MobileButton
                                className="w-full"
                                variant="secondary"
                                onClick={handleCaptureLocation}
                            >
                                Retry
                            </MobileButton>
                        </div>
                    </div>
                )}
            </MobileBottomSheet>
        </div>
    );
}

function ActionButton({
    icon,
    label,
    onClick,
    disabled,
}: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="mobile-card !p-3 flex flex-col items-center gap-1.5 text-primary disabled:opacity-40 active:bg-accent"
        >
            {icon}
            <span className="text-[11px] text-muted-foreground">{label}</span>
        </button>
    );
}

function Detail({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-sm truncate">{value}</p>
        </div>
    );
}

function TimelineItem({ activity }: { activity: LeadActivity }) {
    const Icon =
        activity.activity_type === 'call'
            ? Phone
            : activity.activity_type === 'visit'
              ? MapPin
              : activity.activity_type === 'whatsapp'
                ? MessageCircle
                : activity.activity_type === 'conversion'
                  ? CheckCircle2
                  : activity.activity_type === 'photo'
                    ? Camera
                    : activity.activity_type === 'status_change'
                      ? ArrowRight
                      : FileText;

    return (
        <div className="flex gap-3">
            <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 w-px bg-border mt-1" />
            </div>

            <div className="flex-1 min-w-0 pb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    {activity.new_status && (
                        <span className="text-xs font-medium">
                            {activity.old_status
                                ? `${LEAD_STATUS_LABELS[activity.old_status]} → ${LEAD_STATUS_LABELS[activity.new_status]}`
                                : LEAD_STATUS_LABELS[activity.new_status]}
                        </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(activity.created_at)}
                    </span>
                </div>

                {activity.notes && (
                    <p className="text-sm mt-0.5 whitespace-pre-wrap">{activity.notes}</p>
                )}

                <div className="flex items-center gap-3 mt-1">
                    {activity.created_by_name && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <UserRound className="h-3 w-3" />
                            {activity.created_by_name}
                        </span>
                    )}
                    {activity.geo_location && (
                        <a
                            href={geoLocationToMapsUrl(activity.geo_location)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary"
                        >
                            <MapPin className="h-3 w-3" />
                            At location
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}
